import type { InnspelClient } from '@innspel/core';
import { createContext, useCallback, useContext, useEffect, useInsertionEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { standardAdapter } from './skjermbilde/renderer.ts';
import type { ScreenshotAdapter } from './skjermbilde/typer.ts';
import { ensureStyles } from './tema.ts';
import { Widget, type SubmittedInfo, type Draft, type OpenOptions } from './widget/Widget.tsx';

interface InnspelKontekst {
  client: InnspelClient;
  /** Opens the dialog. Same as `client.openFeedback()`. */
  open: (options?: OpenOptions) => Promise<void>;
  close: () => void;
  isOpen: boolean;
}

const Kontekst = createContext<InnspelKontekst | null>(null);

export interface InnspelProviderProps {
  client: InnspelClient;
  children?: ReactNode;
  /** The name shown on the reply in «Mine innspill» and in «Logg inn på nytt hos …» — the tenant's, not Innspel's. */
  tenantName?: string;
  /** Palette for the dialog. Default is light; `dark` sets `data-innspel-theme="dark"` on the dialog. */
  colorScheme?: 'dark' | 'light';
  /**
   * Capture of the page for «Ta bilde av siden» (capture the page). Default is the package's
   * own renderer (SVG foreignObject, zero dependencies). `false` removes the button; «Last opp
   * bilde» (upload an image) still exists when the tenant has screenshots on. The step does
   * not exist at all when `screenshotsEnabled` is off (DD-26, K18).
   */
  screenshot?: ScreenshotAdapter | false;
  /**
   * The tenant's own sentence at the image step, e.g. «Legg ved et skjermbilde av feilen.
   * Ikke send bilder av andre personer.» Shown next to the buttons and in the preview. One
   * string applies to both tracks; `{ feil, onske }` gives each track its own. The tenant
   * knows its users; the platform does not own that sentence.
   */
  imageStepNote?: string | { feil?: string; onske?: string };
  /** The stylesheet is injected into `<head>` once. `false` when the host imports the CSS itself. */
  injectStyles?: boolean;
  onIdentityExpired?: (utkast: Draft) => void;
  onSendAnonymously?: () => void;
  /**
   * Called when a submission has been sent — or queued because the network was gone (F26).
   * `type`, `queued`, and `id` and `number` when the case exists on the server. No identity:
   * `number` is the case number the submitter already sees in the receipt («Sak 10042»). Use it
   * to refetch the count on «Mine innspill» (my submissions), or to ask for notifications at the
   * right moment — right after the first submission, not at start-up. Not called for
   * «Ja, jeg også».
   */
  onSubmitted?: (info: SubmittedInfo) => void;
}

/**
 * Wrap the page in this, and the dialog exists. The provider registers the presenter with
 * the core (so `client.openFeedback()` also works outside React), keeps open/closed
 * state, and renders the dialog last in the tree. The host's button is the host's (DD-21) —
 * the package never draws a floating button.
 *
 * @example
 * ```tsx
 * const innspel = init({ …, context: webContext({ appVersion: '2.4.1' }), storage: createStorage() });
 * <InnspelProvider client={innspel}><App /></InnspelProvider>
 * ```
 */
export function InnspelProvider({ client, children, tenantName, colorScheme, screenshot, imageStepNote, injectStyles = true, onIdentityExpired, onSendAnonymously, onSubmitted }: InnspelProviderProps) {
  const [isOpen, setOpen] = useState(false);
  const [opsjoner, setOpsjoner] = useState<OpenOptions | undefined>();
  const utløser = useRef<HTMLElement | null>(null);

  useInsertionEffect(() => {
    if (injectStyles) ensureStyles();
  }, [injectStyles]);

  const open = useCallback(async (options?: OpenOptions) => {
    // Fokus skal tilbake til det som åpnet dialogen (DD-47) — vertens knapp.
    utløser.current = (document.activeElement as HTMLElement | null) ?? null;
    setOpsjoner(options);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Registreres på nytt når klienten byttes (portalen bytter ved innlogging).
  useEffect(() => {
    client.registerPresenter({ open });
  }, [client, open]);

  const verdi = useMemo<InnspelKontekst>(() => ({ client, open, close, isOpen }), [client, open, close, isOpen]);
  const adapter = screenshot === false ? null : (screenshot ?? standardAdapter);

  return (
    <Kontekst.Provider value={verdi}>
      {children}
      <Widget
        client={client}
        open={isOpen}
        onClose={close}
        {...(opsjoner ? { apneOpsjoner: opsjoner } : {})}
        {...(tenantName ? { tenantName } : {})}
        screenshot={adapter}
        {...(imageStepNote ? { imageStepNote } : {})}
        {...(colorScheme ? { colorScheme } : {})}
        returnFocusTo={utløser.current}
        {...(onIdentityExpired ? { onIdentityExpired } : {})}
        {...(onSendAnonymously ? { onSendAnonymously } : {})}
        {...(onSubmitted ? { onSubmitted } : {})}
      />
    </Kontekst.Provider>
  );
}

/**
 * The client and `open` from the nearest {@link InnspelProvider}. Throws outside the
 * provider: a button that silently does nothing is worse than an error during
 * development (tech-decisions.md, «Feil bruk feiler høyt og tidlig»).
 */
export function useInnspel(): InnspelKontekst {
  const k = useContext(Kontekst);
  if (!k) {
    throw new Error('[innspel] useInnspel() was called outside <InnspelProvider>. Wrap your page in it — see README.md.');
  }
  return k;
}
